<template>
  <div class="voice-analysis-banner">
    <template v-if="isProgress">
      <BLayout
        class="progress"
        align-center
        justify-center
      >
        <div class="message">
          {{ progressMessage }}
        </div>
      </BLayout>
    </template>
    <template v-else-if="isError">
      <BLayout
        class="failure"
        align-center
        justify-center
      >
        <span class="message">
          {{ errorMessageAndDate }}
        </span>
        <BBtn
          class="clear-btn"
          type="white"
          size="small"
          flat
          fab
          @click="handleDismiss"
        >
          <BIcon size="small">
            clear
          </BIcon>
        </BBtn>
      </BLayout>
    </template>
    <template v-else-if="isSuccess">
      <BLayout
        class="success"
        align-center
        justify-center
      >
        <span class="message">
          {{ successMessageAndDate }}
        </span>
        <BBtn
          class="clear-btn"
          type="white"
          size="small"
          flat
          fab
          @click="handleDismiss"
        >
          <BIcon size="small">
            clear
          </BIcon>
        </BBtn>
      </BLayout>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { altText } from '@/utils/alt';
import { formatMonthDayTime } from '@/utils/date-time';

export interface TargetTask {
  id: number;
  finishedAt?: string | null;
  errorData?: string | null;
  createdAt?: string;
  calleeDidNumber?: string;
}

const props = defineProps<{
  targetTask: TargetTask;
}>();

const emit = defineEmits<{
  dismiss: [];
}>();

const { t } = useI18n();

const getDateWithDateAndTimeFilter = (date?: string) => {
  if (!date) return '--/-- --:--';
  return altText(formatMonthDayTime(date));
};

const isProgress = computed(() => {
  return !!props.targetTask?.id && !props.targetTask.finishedAt;
});

const isError = computed(() => {
  return !!props.targetTask?.id && !!props.targetTask.errorData;
});

const isSuccess = computed(() => {
  const existId = typeof props.targetTask?.id === 'number';
  const existFinishedAt = typeof props.targetTask?.finishedAt === 'string' && props.targetTask.finishedAt.length > 0;
  const noError = props.targetTask?.errorData == null;
  return existId && existFinishedAt && noError;
});

const progressMessage = computed(() => {
  const phoneNumber = props.targetTask?.calleeDidNumber || '---';
  const date = getDateWithDateAndTimeFilter(props.targetTask.createdAt);
  const executionDate = t('vaApp.voiceAnalysis.banner.progress.executionDate', { date });
  const message = t('vaApp.voiceAnalysis.banner.progress.message', { phoneNumber });
  return `${executionDate}${message}`;
});

const errorMessageAndDate = computed(() => {
  const phoneNumber = props.targetTask?.calleeDidNumber || '---';
  const date = getDateWithDateAndTimeFilter(props.targetTask.createdAt);
  const executionDate = t('vaApp.voiceAnalysis.banner.error.executionDate', { date });
  const message = t('vaApp.voiceAnalysis.banner.error.message', { phoneNumber });
  const retryMessage = t('vaApp.voiceAnalysis.banner.error.retryMessage');
  return `${executionDate}${message}\n${retryMessage}`;
});

const successMessageAndDate = computed(() => {
  const phoneNumber = props.targetTask?.calleeDidNumber || '---';
  const date = getDateWithDateAndTimeFilter(props.targetTask.createdAt);
  const executionDate = t('vaApp.voiceAnalysis.banner.success.executionDate', { date });
  const message = t('vaApp.voiceAnalysis.banner.success.message', { phoneNumber });
  return `${executionDate}${message}`;
});

const handleDismiss = () => {
  emit('dismiss');
};
</script>

<style lang="scss" scoped>
.voice-analysis-banner {
  position: relative;
  text-align: center;
  white-space: pre-wrap;

  .clear-btn {
    position: absolute;
    right: $basespace-400;
  }

  .success {
    padding: $basespace-200 0;
    background-color: $basecolor-success;
    @include m-fixed-height(60px);

    .message {
      color: $textcolor-white;
      font-size: $fontsize-300;
    }
  }
  .progress {
    padding: $basespace-400 0;
    background-color: $basecolor-warning;
    @include m-fixed-height(60px);

    .message {
      font-size: $fontsize-300;
      animation: Flash 3s infinite;
    }
  }
  .failure {
    padding: $basespace-200 0;
    background-color: $basecolor-error;
    @include m-fixed-height(60px);

    .message {
      font-size: $fontsize-300;
      color: $textcolor-white;
    }
  }
}

@keyframes Flash {
  50% {
    opacity: 0.1;
  }
}
</style>
